/**
 * slice.js — локальная нарезка «широкого» ответа /level-funnel/dataset
 * =======================================================================
 *
 * Чистые функции без React. Сервер отдаёт все горизонты × все уровни ×
 * (при mode='absorption') все N бустеров одним ответом; сеть дёргается
 * только при смене режима/фильтров (metricsService.getLevelFunnelDataset).
 * Горизонт, диапазон уровней, галку «от инсталлов» и N бустеров режем тут,
 * в useMemo на стороне виджета — чтобы не трогать LevelFunnelTable/Charts/
 * AbsorptionTimingCharts, которые ждут ровно ту форму, что раньше отдавали
 * старые эндпоинты /level-funnel/table и /level-funnel/absorption-timing.
 *
 * Не мутируем dataset — только читаем и собираем новые объекты (проектное
 * правило иммутабельности).
 */

import { levelFunnel as levelFunnelSpecs } from '@ggstat/chart-specs';

// Сколько уровней показываем максимум — общее с MCP (levelFunnel.json → controls).
const LEVEL_CLAMP_SIZE = levelFunnelSpecs.controls.levelClampSize;

/** Достаёт значение горизонта из HorizonMap; отсутствующий горизонт → null/0. */
function atHorizon(map, horizon, fallback = 0) {
  if (!map) return fallback;
  const v = map[horizon];
  return v === undefined ? fallback : v;
}

/** Срезает LevelFunnelGroupStats (все поля — HorizonMap) в плоский объект чисел. */
function sliceGroupStats(group, horizon) {
  if (!group) return null;
  const sliced = {
    uniqueStarters: atHorizon(group.uniqueStarters, horizon),
    uniqueWinners: atHorizon(group.uniqueWinners, horizon),
    uniqueLosers: atHorizon(group.uniqueLosers, horizon),
    starts: atHorizon(group.starts, horizon),
    wins: atHorizon(group.wins, horizon),
    losses: atHorizon(group.losses, horizon)
  };
  if (group.skips) sliced.skips = atHorizon(group.skips, horizon);
  if (group.uniqueSkippers) sliced.uniqueSkippers = atHorizon(group.uniqueSkippers, horizon);
  return sliced;
}

/** Срезает row.absorption (все поля — HorizonMap, кроме stars/timeLimits — HorizonMap<array>). */
function sliceAbsorption(absorption, horizon) {
  if (!absorption) return undefined;
  return {
    medianSec: atHorizon(absorption.medianSec, horizon, null),
    avgSec: atHorizon(absorption.avgSec, horizon, null),
    starsAvg: atHorizon(absorption.starsAvg, horizon, null),
    stars: atHorizon(absorption.stars, horizon, [0, 0, 0, 0]),
    timeLimitSec: atHorizon(absorption.timeLimitSec, horizon, null),
    timeLimits: atHorizon(absorption.timeLimits, horizon, []),
    winsWithBoosters: atHorizon(absorption.winsWithBoosters, horizon)
  };
}

/** Срезает одну строку LevelFunnelRow на конкретный горизонт. */
function sliceRow(row, horizon) {
  const groups = {};
  Object.keys(row.groups || {}).forEach((key) => {
    groups[key] = sliceGroupStats(row.groups[key], horizon);
  });
  const sliced = {
    level: row.level,
    uniqueStartersAny: atHorizon(row.uniqueStartersAny, horizon),
    groups
  };
  const absorption = sliceAbsorption(row.absorption, horizon);
  if (absorption) sliced.absorption = absorption;
  return sliced;
}

/**
 * Сдвиг даты 'YYYY-MM-DD' на N дней (UTC, как серверный shiftDays) —
 * нужен только для сравнения строк, поэтому возвращаем тоже строку.
 */
function shiftDays(dateStr, days) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/**
 * Когорта «не дозрела» для выбранного горизонта: конец окна ещё не отстоит от
 * today() на нужное число дней. Сравнение строк 'YYYY-MM-DD' лексикографическое
 * (работает для одинакового формата дат). Логика — 1:1 с серверным вычислением,
 * которое раньше жило в levelFunnelService.ts до перехода на «широкий» ответ.
 */
function computeImmature(windowInfo, horizon) {
  if (!windowInfo || horizon === 'all') return { immature: false, immatureDays: 0 };
  const immatureDays = Number(horizon);
  const cutoff = shiftDays(windowInfo.today, -immatureDays);
  return { immature: windowInfo.end > cutoff, immatureDays };
}

/**
 * База воронки для строки level === levelFrom (до применения fromInstalls).
 * Строки здесь — СЫРЫЕ, из набора, поэтому поля ещё HorizonMap: значение
 * обязательно снимаем горизонтом, иначе наружу уйдёт объект вместо числа и
 * все проценты воронки превратятся в NaN.
 */
function baseFromRows(rows, levelFrom, mode, horizon) {
  const row = rows.find((r) => r.level === levelFrom);
  if (!row) return 0;
  if (mode === 'td') return atHorizon(row.uniqueStartersAny, horizon);
  return atHorizon(row.groups?.all?.uniqueStarters, horizon);
}

/**
 * Нарезает dataset в форму, которую раньше отдавали /level-funnel/table и
 * /level-funnel/absorption-timing.
 * @param {Object|null} dataset - Ответ MetricsService.getLevelFunnelDataset
 * @param {Object} opts - { horizon, levelFrom, levelTo, fromInstalls, boosterN }
 * @param {boolean} droppedFilters - фильтры кампании/проекта/источника активны
 *   на дашборде (таблица событий их не поддерживает); считается виджетом по
 *   filters дашборда, не по ответу сервера — droppedFilters в контракте больше нет.
 * @returns {{ tableData: Object|null, timingData: Object|null }}
 */
export function sliceDataset(dataset, opts, droppedFilters = false) {
  if (!dataset) return { tableData: null, timingData: null };

  const { horizon, fromInstalls, boosterN, mode } = opts;
  let { levelFrom, levelTo } = opts;
  if (levelFrom > levelTo) {
    [levelFrom, levelTo] = [levelTo, levelFrom];
  }

  const allRows = dataset.levels || [];
  const inRange = allRows.filter((r) => r.level >= levelFrom && r.level <= levelTo);

  let shownFrom = levelFrom;
  let shownTo = levelTo;
  let levelClamped = false;
  let visibleRows = inRange;
  if (levelTo - levelFrom >= LEVEL_CLAMP_SIZE) {
    levelClamped = true;
    shownTo = shownFrom + LEVEL_CLAMP_SIZE - 1;
    visibleRows = inRange.filter((r) => r.level <= shownTo);
  }

  const rows = visibleRows.map((row) => sliceRow(row, horizon));

  const base = fromInstalls
    ? dataset.installs?.count ?? 0
    : baseFromRows(allRows, levelFrom, mode, horizon);
  const { immature, immatureDays } = computeImmature(dataset.window, horizon);

  const tableData = {
    mode,
    fromInstalls,
    boosterN,
    levelFrom: shownFrom,
    levelTo: shownTo,
    levelClamped,
    horizon,
    base,
    immature,
    immatureDays,
    rows
  };
  if (fromInstalls) {
    tableData.installs = { ...dataset.installs, droppedFilters };
  }

  let timingData = null;
  if (mode === 'absorption' && dataset.timing) {
    const timingInRange = dataset.timing.filter((r) => r.level >= levelFrom && r.level <= levelTo);
    const visibleTiming = levelClamped
      ? timingInRange.filter((r) => r.level <= shownTo)
      : timingInRange;
    timingData = {
      mode: 'absorption',
      boosterN,
      levelFrom: shownFrom,
      levelTo: shownTo,
      rows: visibleTiming.map((row) => {
        const boosterGroup = row.byBoosterN?.[String(boosterN)];
        return {
          level: row.level,
          timeLimitSec: atHorizon(row.timeLimitSec, horizon, null),
          timeLimits: atHorizon(row.timeLimits, horizon, []),
          groups: {
            g0: sliceTimingGroup(row.g0, horizon),
            gMid: sliceTimingGroup(boosterGroup?.gMid, horizon),
            gHigh: sliceTimingGroup(boosterGroup?.gHigh, horizon)
          }
        };
      })
    };
  }

  return { tableData, timingData };
}

/** Срезает TimingGroup (medianSec/avgSec/wins/stars — все HorizonMap) на горизонт. */
function sliceTimingGroup(group, horizon) {
  if (!group) return { medianSec: null, avgSec: null, wins: 0, stars: [0, 0, 0, 0] };
  return {
    medianSec: atHorizon(group.medianSec, horizon, null),
    avgSec: atHorizon(group.avgSec, horizon, null),
    wins: atHorizon(group.wins, horizon),
    stars: atHorizon(group.stars, horizon, [0, 0, 0, 0])
  };
}

export default sliceDataset;

/**
 * Домен оси X по выбранному диапазону уровней, а не по крайним строкам данных:
 * без явного домена recharts рисует числовую ось от 0. ±0.5 — чтобы крайние
 * столбцы bar-графиков не обрезались о границу оси.
 */
export function levelAxisDomain(tableData) {
  if (!tableData) return ['dataMin', 'dataMax'];
  return [tableData.levelFrom - 0.5, tableData.levelTo + 0.5];
}
